import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import ReplayIcon from "@mui/icons-material/Replay";
import ClearIcon from "@mui/icons-material/Clear";
import { Alert, Autocomplete, Box, Card, CardContent, CardHeader, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, Grid, IconButton, InputLabel, List, 
  ListItem, MenuItem, Radio, RadioGroup, Select, Stack, Switch, SxProps, TextField, Theme, Tooltip, Typography } from "@mui/material";
import { useGetMainList, useGetOne, useRecordContext } from "ra-core";
import { FC, ReactElement, useEffect, useMemo, useState } from "react";
import * as Yup from 'yup';
import { usePlainStateFormUtils } from "@/components/input/usePlainStateFormUtils";
import { useLmStatesPropagate } from "@/contexts/LocalMessageHookProtocols/useLmStatesPropagate";
import { LocalMessageLocation } from "@/contexts/LocalMessagingRoot";
import { DataInfoTypeResponseDto, DataKindCategory, DataType, DetectPattern, EncryptionRule, KeywordRule } from "@/gen/api/encrypt";
import { promiseWithResolvers } from "@/utils/promiseWithResolvers";
import StringArrayTextField from "@/components/input/StringArrayTextField";
import { PrimaryClassification, SecondaryClassificationResponseDto } from "@/gen/api/governance";
import { SimpleOption } from "@/components/governance/search/AmbientFacilitiesContext";
import { RecPatch } from "@/dataProvider/tools";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { FileDataType } from "@/pages/obfuscation/FileTemplateDetail.hooks";



const topSchema = Yup.object({
  name: Yup.string().required('Name is required').max(100, 'Name must be at most 100 characters')
    .test('is-valid-chars', 'Name can only contain letters, numbers, underscores(_), or hyphens(-).', (x:string) => {
      return /^[a-zA-Z0-9_-]+$/.test(x)
    }),
  displayName: Yup.string().required('Display Name is required').max(100, 'Display Name must be at most 100 characters'),
  description: Yup.string().max(512, 'Description must be at most 512 characters'),
  defaultSecondClassification: Yup.number().required('Classification is required'),
  defaultEncryptionRule: Yup.number().required('Encryption Rule is required'),
  minLength: Yup.number().required('Minimum Length is required'),
})

  const remapDataTypes = (record: DataInfoTypeResponseDto) => {
    const o = ({
      date: record.dataTypes?.some(d => d === DataType.Date) ?? false,
      string: record.dataTypes?.some(d => d === DataType.String) ?? false,
      number: record.dataTypes?.some(d => d === DataType.Number) ?? false,
    })
    if (!o.string && !o.date && !o.number) { o.string = true }
    return o
  }
  const initialVal = (record: DataInfoTypeResponseDto) => {
    const dataTypes = remapDataTypes(record)
    return ({ 
      prevData: record,
      stage: {
        name: record.name,
        displayName: record.displayName,
        description: record.description,
        dataTypes,
        minLength: record.minLength ?? 8,
        defaultSecondClassification: record.defaultSecondClassification,
        defaultEncryptionRule: record.defaultEncryptionRule,
        isEnabled: record.isEnabled ?? true,
      },
      detectPatternCount: record.detectPatterns?.length ?? 0,
      keywordRulesCount: record.keywordRules?.length ?? 0,
      countTouched: false,
      errors: {} as any,
      touched: {} as any,
      dirtySet: new Set<string>(),
    })
  }

const DataInfoTypeEditor: FC<{ title: string, disabled?: boolean }> = ({ title, disabled }) => {
  const record = useRecordContext<DataInfoTypeResponseDto>() ?? emptyInfoTypeDto
  // const { data: baseInfoTypeData, } = useGetMainList(baseInfoTypeRes, pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY' })
  const createMode = record === emptyInfoTypeDto
  
  const { classifications, ruleMap } = useOptionsData()

  const [states, setStates] = useState(() => initialVal(record))
  useEffect(() => setStates(initialVal(record)), [record])
  const { validate, fieldProps } = usePlainStateFormUtils(states, setStates, topSchema)

  const {informDirty} = useLmStatesPropagate({ 
    key: 'editor',
    asUpperStage: {
      dirtyStateInformed: {
        handler(informDirty) {
          return (subStateKey, dirty) => {
            setStates(v => {
              dirty ? v.dirtySet.add(subStateKey) : v.dirtySet.delete(subStateKey)
              informDirty(editorDirty(v))
              return v
            })
          }
        },
        deps: []
      }
    },
    asLowerStage: {
      collectStates: {
        handler(assembleSubStates) {
          return async () => {
            if (disabled) { return Promise.reject('') }
            const qV1 = promiseWithResolvers<typeof states>()
            setStates(v => {
              const errors = validate(v.stage) ?? {}
              const ks = Object.keys(v.stage)
              const v1 = {...v, errors, touched: ks.reduce((acc, k) => ({...acc, [k]:true}), {} as any)}
              qV1.resolve(v1)
              return v1
            })
            const v1 = await qV1.promise
            const ks = Object.keys(v1.errors)
            if (ks.length > 0) {
              return Promise.reject('')
            }
            try {
              const result = await assembleSubStates(
                v1.prevData.isBuiltIn || !v1.stage.dataTypes.string
                ? ['keywordRules']
                : ['detectPatterns', 'keywordRules']
              )
              
              const payload = {...result, ...v1.stage,
                dataTypes: Object.keys(v1.stage.dataTypes).flatMap(k => (v1.stage.dataTypes as any)[k] ? dataTypeMap[k] : [])
              }
              const qV2 = promiseWithResolvers<typeof states>()
              setStates(v => {
                const v2 = {...v, detectPatternCount: payload.detectPatterns?.length ?? 0, keywordRulesCount: payload.keywordRules?.length ?? 0, countTouched: true}
                qV2.resolve(v2)
                return v2
              })
              const v2 = await qV2.promise
              if (!v2.prevData.isBuiltIn && v2.keywordRulesCount < 1) {
                return Promise.reject('')
              }
              return Promise.resolve(payload)
              
            } catch (error) {
              return Promise.reject('')
            }
          }
        },
        deps: [disabled]
      }
    }
  })

  const editorDirty = (v: typeof states) => {
    if (v.dirtySet.size > 0
      || v.stage.defaultSecondClassification !== v.prevData.defaultSecondClassification
      || v.stage.defaultEncryptionRule !== v.prevData.defaultEncryptionRule
      || v.stage.isEnabled !== v.prevData.isEnabled
      || v.stage.name !== v.prevData.name
      || v.stage.displayName !== v.prevData.displayName
      || v.stage.description !== v.prevData.description
    ) { return true }
    const prevDataTypes = remapDataTypes(v.prevData)
    return (v.stage.dataTypes.date !== prevDataTypes.date
      || v.stage.dataTypes.string !== prevDataTypes.string
      || v.stage.dataTypes.number !== prevDataTypes.number
      || (prevDataTypes.string && (v.stage.minLength !== v.prevData.minLength))
    )
  }
  const isDirty = editorDirty(states)
  informDirty(isDirty)
  const rules = useMemo(() => {
    return (
      states.stage.dataTypes.date   ? ruleMap[FileDataType.Date]
    : states.stage.dataTypes.number ? (
      states.stage.dataTypes.string 
      ? ruleMap[FileDataType.Numeric].filter(r => r.name !== 'Integer Number')
      : ruleMap[FileDataType.Numeric]
    )
    : ruleMap[FileDataType.String]) ?? []
  }, [states.stage.dataTypes, ruleMap])

  const findClassification = (clssId: number|undefined) => {
    return (clssId &&
      classifications.find(c => (c as SecondaryClassificationResponseDto).secondaryClassificationId === clssId))
  }
  const encryptor = rules.find(r => r.id === states.stage.defaultEncryptionRule)
  
  const changeDataType = (s: string) => (e: any, checked: boolean) => {
    setStates(v => {
      if (!checked) {
        if (Object.values(v.stage.dataTypes).filter(x => x).length === 1) { return v }
      }
      const v1 = ({...v, stage: {...v.stage, dataTypes: {...v.stage.dataTypes, [s]: checked}}})
      if (s === 'number' && checked) { v1.stage.dataTypes['date'] = false; v1.stage.dataTypes['string'] = false }
      if (s === 'date' && checked) { v1.stage.dataTypes['number'] = false }
      if (s === 'string' && checked) { v1.stage.dataTypes['number'] = false }
      informDirty(editorDirty(v1))
      return v1
    })
  }
  return (
    <Card>
      <CardHeader title={title} action={
        !createMode &&
          <FormControlLabel label={states.stage.isEnabled ? 'Active' : 'Inactive'} disabled={disabled}
            control={
              <Switch checked={states.stage.isEnabled} size="small" color="primary"
                onChange={(e, isEnabled) => setStates(v => {
                  const v1 = ({...v, stage: {...v.stage, isEnabled }})
                  informDirty(editorDirty(v1))
                  return v1
                })}
              />}
          />
      }/>
      <CardContent>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <TextField label="Name*" disabled={!createMode || record.isBuiltIn || disabled} multiline fullWidth {...fieldProps('name')}/>
          </Grid>
          <Grid item xs={6}>
            <TextField label="Display Name*" disabled={record.isBuiltIn || disabled} fullWidth multiline {...fieldProps('displayName')}/>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Description" disabled={record.isBuiltIn || disabled} fullWidth multiline {...fieldProps('description')}/>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" width="100%" alignItems="center">
              <FormControl sx={{ mx: 2 }} component="fieldset" variant="standard">
                <FormLabel component="legend" sx={{ display: 'flex', alignItems: 'start', flexDirection: 'row' }}>
                  Data Types
                  <Tooltip title="This Data Info Type can match the following data types in databases. All character-based data types are considered as String." >
                    <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                  </Tooltip>
                </FormLabel>
                <FormGroup row={true}>
                  <FormControlLabel
                    control={
                      <Checkbox name="string-type" checked={states.stage.dataTypes.string} onChange={changeDataType('string')} />}
                    label="String"
                    disabled={record.isBuiltIn || disabled}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox name="number-type" checked={states.stage.dataTypes.number} onChange={changeDataType('number')} />}
                    label="Number"
                    disabled={record.isBuiltIn || disabled}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox name="date-type" checked={states.stage.dataTypes.date} onChange={changeDataType('date')} />}
                    label="Date"
                    disabled={record.isBuiltIn || disabled}
                  />
                </FormGroup>
              </FormControl>
              {states.stage.dataTypes.string &&
                <TextField disabled={record.isBuiltIn || disabled} variant="standard" size="small" sx={{ ml: 8, width: 120, '& p.MuiFormHelperText-root.Mui-error': { width: 200 } }} 
                  label={
                    <Stack direction="row" alignItems="start">
                      Minimum Length
                      <Tooltip title="For String data types, this Data Info Type can only match data with a minimum number of characters as defined.">
                        <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                      </Tooltip>
                    </Stack>} 
                  type="number"
                  {...fieldProps('minLength', (x:any) => {
                    if (!x) { return undefined }
                    let n = +x
                    if (Number.isNaN(n)) { return 8 }
                    n = Math.floor(n)
                    return (n < 1 ? 1 : n)
                  })} 
                />}
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete 
              disabled={disabled}
              disableClearable={true}
              options={classifications}
              getOptionLabel={(option) => option ? (option.text || '') : ''}
              value={(findClassification(states.stage.defaultSecondClassification) || '' as any)}
              
              renderInput={((params) =>
                <TextField 
                  label="Classification"
                  {...params} 
                  fullWidth 
                  error={Boolean(states.touched['defaultSecondClassification'] && states.errors['defaultSecondClassification'])}
                  helperText={states.touched['defaultSecondClassification'] && states.errors['defaultSecondClassification']}
                  {...{
                    inputProps: {
                      ...params.inputProps,
                      'aria-label': 'Classification',
                    }
                  }}
                />
              )}
              onChange={(event, value, reason) => {
                if (reason === 'selectOption') {
                  const cls1 = value as SecondaryClassificationResponseDto
                  setStates(v => {
                    const v1 = ({...v,
                      stage: {...v.stage, defaultSecondClassification: cls1.secondaryClassificationId },
                      errors: (delete v.errors['defaultSecondClassification'], v.errors),
                      touched: {...v.touched, defaultSecondClassification: true},
                    })
                    informDirty(editorDirty(v1))
                    return v1
                  })
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ width: '100%' }}>
              <FormControl fullWidth>
                <InputLabel id={`encryption-rule-label`}>Encryption Rule</InputLabel>
                <Select
                  disabled={disabled}
                  label="Encryption Rule"
                  labelId={`encryption-rule-label`}
                  id={`encryption-rule-select`}
                  error={Boolean(states.touched['defaultEncryptionRule'] && states.errors['defaultEncryptionRule'])}
                  value={states.stage.defaultEncryptionRule || ''}
                  onChange={(e) => setStates(v => {
                    const v1 = ({...v, 
                      stage: {...v.stage, defaultEncryptionRule: +e.target.value},
                      errors: (delete v.errors['defaultEncryptionRule'], v.errors),
                      touched: {...v.touched, defaultEncryptionRule: true},
                    })
                    informDirty(editorDirty(v1))
                    return v1
                  })}
                  sx={{ minWidth: 80 }}
                >
                  {rules.map(r => (
                    <MenuItem key={r.id} value={r.id as any}>{r.name}</MenuItem>
                  ))}
                </Select>
                {states.touched['defaultEncryptionRule'] && states.errors['defaultEncryptionRule'] &&
                  <FormHelperText error>{states.errors['defaultEncryptionRule']}</FormHelperText>}
              </FormControl>
              {encryptor &&
                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                  <Typography variant="caption" sx={{ color: 'primary.light', '& span': { width: 44, display: 'inline-block' } }}>
                    <span>Input: </span>{encryptor.sampleInput}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'success.main' }}>Output: {encryptor.sampleOutput}</Typography>
                </Box>}
            </Box>

          </Grid>
          {states.countTouched && (states.keywordRulesCount < 1) &&
            <Grid item xs={12}>
              <FormHelperText error>Should define at least 1 rule in Context Rules</FormHelperText>
            </Grid>}
          {!record.isBuiltIn && states.stage.dataTypes['string'] &&
            <Grid item xs={12}>
              <LocalMessageLocation id="detectPatterns">
                <DetectPatternEditor baseData={states.prevData.detectPatterns!} disabled={disabled} />
              </LocalMessageLocation>
            </Grid>}
          <Grid item xs={12}>
            <LocalMessageLocation id="keywordRules">
              <KeywordRuleEditor prevData={states.prevData.keywordRules!} disabled={disabled} />
            </LocalMessageLocation>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default DataInfoTypeEditor;

const emptyInfoTypeDto : DataInfoTypeResponseDto = Object.freeze({
  name: '',
  displayName: '',
  description: '',
  isBuiltIn: false,
  dataTypes: Object.freeze([]) as any,
  minLength: 8,
  detectPatterns: Object.freeze([]) as any,
  validateFunction: '',
  keywordRules: Object.freeze([]) as any,
  defaultSecondClassification: undefined as number|undefined,
  defaultEncryptionRule: undefined as number|undefined,
  isEnabled: false,
})

const dataTypeMap : Record<string, DataType[]> = {
  string: [DataType.String],
  number: [DataType.Number],
  date: [DataType.Date],
}

const EditorLineSx : SxProps<Theme> = {
  '& li.MuiListItem-root': {
    pl: 3,
    '&:hover': {
      bgcolor: theme => theme.palette.action.hover,
    },
    '&::before': {
      display: 'block',
      content: "''",
      position: 'absolute',
      left: 0,
      height: 'calc(100% - 2px)',
      width: 4,
      bgcolor: 'divider'
    }
  },
  '& li.MuiListItem-root.added::before': { bgcolor: 'success.light' },
  '& li.MuiListItem-root.removed': {
    '&::before': { bgcolor: 'error.light' },
    '& input, & textarea': { textDecoration: 'line-through' },
  },
  '& li.MuiListItem-root.dirty::before': { bgcolor: 'info.light' },
}

const DetectPatternEditor : FC<{
  baseData: DetectPattern[], disabled?: boolean
}> = ({baseData, disabled}) => {
  const [states, setStates] = useState({
    prevData: baseData,
    stage: {
      added: [] as DetectPattern[], // sparse array
      removed: new Set<number>(),
    },
    touched: false,
    dirtySet: new Set<string>(),
  })
  useEffect(() => {
    setStates(v => ({...v, prevData: baseData, stage: {added: [], removed:new Set()}, touched: false, dirtySet: new Set<string>() }))
  }, [baseData])
  
  const {informDirty} = useLmStatesPropagate({
    key: 'detectPatterns',    
    asUpperStage: {
      dirtyStateInformed: {
        handler(informDirty) {
          return (subStateKey, dirty) => {
            setStates(v => {
              if (subStateKey.startsWith('r')) {
                dirty ? v.dirtySet.add(subStateKey) : v.dirtySet.delete(subStateKey)
              }
              informDirty(v.stage.removed.size > 0 || v.dirtySet.size > 0 || v.stage.added.some(x => !!x))
              return v
            })
          }
        },
        deps: []
      }
    },
    asLowerStage: {
      collectStates: {
        handler(assembleSubStates) {
          return async () => {
            const qV = promiseWithResolvers<typeof states>()
            setStates(v => { 
              qV.resolve(v)
              return v 
            })
            const v = await qV.promise
            const subStatesKeys = [
              ...[...new Array(v.prevData.length)].map((x, i) => i).filter(i => !v.stage.removed.has(i)).map(i => `r[${i}]`),
              ...v.stage.added.map((x, i) => i).map(i => `a[${i}]`).filter(x => x != null)
            ]
            try {
              // no detectPatterns defined is allowed
              if (subStatesKeys.length === 0) { return Promise.resolve([]) }

              const result = await assembleSubStates(subStatesKeys)
              const r = result;
              return Promise.resolve([
                ...(r.a as any as any[] ?? []).flatMap(x => [x.newValue]).reverse(),
                ...(r.r as any as any[] ?? []).flatMap(x => [x.newValue])
              ])
            } catch (err) {
              return Promise.reject('')
            }
          }
        },
        deps: []
      }
    }
  })
  const actionBtn = (d: DetectPattern, i: number) => {
    if (disabled) { return null }
    const atAdded = states.stage.added.indexOf(d)
    const isRestore = states.stage.removed.has(i)
    const actionFn = () => setStates(v => {
      if (atAdded > -1) { // delete added
        delete v.stage.added[atAdded]
      } else if (isRestore) { // restore removed
        v.stage.removed.delete(i)
      } else { // to remove
        v.stage.removed.add(i)
        v.dirtySet.delete(`r[${i}]`)
      }
      informDirty(v.stage.removed.size > 0 || v.dirtySet.size > 0 || v.stage.added.some(x => !!x))
      return {...v, touched: true}
    })
    
    return (
      <Tooltip title={isRestore ? "Restore" : "Remove"}>
        <IconButton color="secondary" onClick={actionFn}>
          {isRestore ? <ReplayIcon /> : <ClearIcon />}
        </IconButton>
      </Tooltip>)
  }
  const classOf = (d: DetectPattern, i: number) => {
    return states.stage.removed.has(i) ? 'removed' : undefined
  }
  
  return (
    <Box
      sx={[EditorLineSx as any, {
        '& li.MuiListItem-root div.line-box': {
          flex: '1 1 auto',
          mr: 2,
          '& .MuiTextField-root:last-of-type': {
            flex: '1 1 auto'
          }
        },
      }]}
    >
      <Stack direction="row" alignItems="center" spacing={4} marginBottom={1}>
        <Typography variant="subtitle2">Pattern Detection</Typography>
        {!disabled &&
          <Tooltip title="Add Pattern">
            <IconButton color="primary"
              onClick={() => setStates(v => {
                v.stage.added.push({ name: '', score: 0.05, regex: ''})
                informDirty(true)
                return {...v, touched: true}
              })} 
            >
              <AddIcon /></IconButton>
          </Tooltip>}
      </Stack>
      <Alert severity="info" sx={{ mb: 1 }}>
        Each pattern match is designated with a matching score. If a piece of content matches multiple patterns, the highest score out of all matches is used.
      </Alert>
      <List disablePadding>
        {states.stage.added.map((d, i) =>(
          <LocalMessageLocation id={`a[${i}]`} key={i}>
            <DetectPatternLine className="added" r_key={`a[${i}]`} prevData={d} disabled={disabled} actionButton={actionBtn(d, i)}/>
          </LocalMessageLocation>
        )).filter(d => !!d).reverse()}
      </List>
      <List disablePadding>
        {states.prevData.map((d, i) => (
          <LocalMessageLocation id={`r[${i}]`} key={i}>
            <DetectPatternLine className={classOf(d, i)} r_key={`r[${i}]`} prevData={d} disabled={disabled} actionButton={actionBtn(d, i)}/>
          </LocalMessageLocation>
        ))}
      </List>
    </Box>
  )
}

const dpSchema = Yup.object({
  name: Yup.string().required('Name is required').max(100, 'Name must be at most 100 characters'),
  score: Yup.number().required('Score is required'),
  regex: Yup.string().required('Regular Expression is required')
    .test('is-valid-regex', 'Regular Expression value is incorrect', (x:string) => {
      try {
        new RegExp(x)
        return true
      } catch {
        return false
      }
    }),
})

const DetectPatternLine : FC<{ 
  className?: string,
  r_key: string,
  prevData: DetectPattern, 
  actionButton: ReactElement|null,
  disabled?: boolean,
}> = ({ className, r_key, prevData, actionButton, disabled }) => {
  const [states, setStates] = useState({
    prevData,
    stage: {...prevData},
    errors: {} as any,
    touched: {} as any,
  })
  useEffect(() => { setStates(v => ({...v, prevData, stage: {...prevData}, errors:{}, touched:{} })) }, [prevData])
  const { validate, fieldProps } = usePlainStateFormUtils(states, setStates, dpSchema)

  const {informDirty} = useLmStatesPropagate({
    key: r_key,
    asLowerStage: {
      collectStates: {
        handler() {
          return () => {
            const {promise, resolve, reject} = promiseWithResolvers()
            setStates(v => {
              const errors = validate(v.stage) ?? {}
              let ks = Object.keys(errors)
              if (ks.length > 0) {
                reject('')
              } else {
                resolve({
                  origin: v.prevData,
                  newValue: (
                    v.prevData.name === v.stage.name &&
                    v.prevData.score === v.stage.score &&
                    v.prevData.regex === v.stage.regex) ? v.prevData : v.stage 
                })
              }
              ks = Object.keys(v.stage)
              return {...v, errors, touched: ks.reduce((acc, k) => ({...acc, [k]:true}), {} as any) }
            })
            return promise
          }
        },
        deps: []
      }
    }
  })
  const isDirty = (
    states.prevData.name !== states.stage.name ||
    states.prevData.score !== states.stage.score ||
    states.prevData.regex !== states.stage.regex)
  informDirty(isDirty)
  return (
    <ListItem className={className ?? (isDirty ? 'dirty' : undefined)}>
      <Stack className="line-box" direction="row" spacing={4} >
        <TextField label="Name" disabled={disabled} variant="standard" size="small" {...fieldProps('name')} multiline/>
        <TextField variant="standard" disabled={disabled} size="small" type="number" sx={{ width: 60, '& p.MuiFormHelperText-root.Mui-error': { width: 200 } }} 
          label={
            <Stack direction="row" alignItems="start">
              Score
              <Tooltip title="A matching score indicates how likely the matching content is actually of this Info Type. The score may be modified by the Context Rules below, if any. A final score of 0.5 or above is deemed that the matching content is of this Info Type.">
                <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
              </Tooltip>
            </Stack>} 
          {...fieldProps('score', (x: any) => {
            if (!x) { return undefined }
            let n = +x
            if (Number.isNaN(n)) { return 0 }
            n = parseFloat(n.toFixed(2))
            return (
                n < 0 ? 0 
              : n > 1 ? 1 
              : n)
          })}
          inputProps={{ step: 0.01, max: 1, min: 0, }}
        />
        <TextField label="Regular Expression" disabled={disabled} variant="standard" size="small" {...fieldProps('regex')} multiline />
      </Stack>
      {actionButton}
    </ListItem>
  )
}

const KeywordRuleEditor : FC<{ prevData: KeywordRule[], disabled?: boolean }> = ({
  prevData, disabled
}) => {
  const [states, setStates] = useState({
    prevData,
    stage: {
      added: [] as KeywordRule[], // sparse array
      removed: new Set<number>(),
    },
    dirtySet: new Set<string>(),
  })
  useEffect(() => {
    setStates(v => ({...v, prevData, stage: {added:[], removed: new Set()}, dirtySet: new Set<string>() }))
  }, [prevData])
  
  const {informDirty} = useLmStatesPropagate({
    key: 'keywordRules',
    asUpperStage: {
      dirtyStateInformed: {
        handler(informDirty) {
          return (subStateKey, dirty) => {
            setStates(v => {
              if (subStateKey.startsWith('r')) {
                dirty ? v.dirtySet.add(subStateKey) : v.dirtySet.delete(subStateKey)
              }
              informDirty(v.stage.removed.size > 0 || v.dirtySet.size > 0 || v.stage.added.some(x => !!x))
              return v
            })
          }
        },
        deps: []
      }
    },
    asLowerStage: {
      collectStates: {
        handler(assembleSubStates) {
          return async () => {
            if (disabled) { return null }
            const qV = promiseWithResolvers<typeof states>()
            setStates(v => { 
              qV.resolve(v)
              return v 
            })
            const v = await qV.promise
            const subStatesKeys = [
              ...[...new Array(v.prevData.length)].map((x, i) => i).filter(i => !v.stage.removed.has(i)).map(i => `r[${i}]`),
              ...v.stage.added.map((x, i) => i).map(i => `a[${i}]`).filter(x => x != null)
            ]
            try {
              // no keywordRules defined is alloweds
              if (subStatesKeys.length === 0) { return [] }

              const result = await assembleSubStates(subStatesKeys)
              const r = result
              return Promise.resolve([
                ...(r.a as any as any[] ?? []).flatMap(x => [x.newValue]).reverse(),
                ...(r.r as any as any[] ?? []).flatMap(x => [x.newValue])
              ])
            } catch (err) {
              return Promise.reject('keywordRules')              
            }
          }
        },
        deps: []
      }
    }
  })
  const actionBtn = (d: KeywordRule, i:number) => {
    if (disabled) { return null }
    const atAdded = states.stage.added.indexOf(d)
    const isRestore = states.stage.removed.has(i)
    const actionFn = () => setStates(v => {
      if (atAdded > -1) { // delete added
        delete v.stage.added[atAdded]
      } else if (isRestore) { // restore removed
        v.stage.removed.delete(i)
      } else { // to remove
        v.stage.removed.add(i)
        v.dirtySet.delete(`r[${i}]`)
      }
      informDirty(v.stage.removed.size > 0 || v.dirtySet.size > 0 || v.stage.added.some(x => !!x))
      return {...v}
    })
    
    return (
      <Tooltip title={isRestore ? "Restore" : "Remove"}>
        <IconButton color="secondary" onClick={actionFn}>
          {isRestore ? <ReplayIcon /> : <ClearIcon />}
        </IconButton>
      </Tooltip>)
  }
  const classOf = (d: KeywordRule, i: number) => {
    return states.stage.removed.has(i) ? 'removed' : undefined
  }

  return (
    <Box
      sx={[EditorLineSx]}
    >
      <Stack direction="row" alignItems="center" spacing={4} marginBottom={1}>
        <Typography variant="subtitle2">Context Rules</Typography>
        {!disabled &&
          <Tooltip title="Add Context Rule">
            <IconButton color="primary"
              onClick={() => setStates(v => {
                v.stage.added.push({ words: [], regex: null, scoreAdjustment: 0.5})
                informDirty(true)
                return {...v}
              })} 
            >
              <AddIcon /></IconButton>
          </Tooltip>}
      </Stack>
      <Alert severity="info" sx={{ mb: 1 }}>
      The context of a Data Info Type is the column name in case of relational databases, or field name in case of NoSQL databases or JSON files. A context rule is used to adjust the matching score derived from the Pattern Detection above. The context is matched with the Content Keywords or Context Pattern below. If any match is found, the matching score derived from Pattern Detection is incremented by the Context Score. If matched by multiple context rules, the matching score is incremented by the highest context score.
      </Alert>
      <List disablePadding>
        {states.stage.added.map((d, i) => (
          <LocalMessageLocation id={`a[${i}]`} key={i}>
            <KeywordRuleLine className="added" r_key={`a[${i}]`} prevData={d} disabled={disabled} actionButton={actionBtn(d, i)} />
          </LocalMessageLocation>
        )).filter(d => !!d).reverse()}
      </List>
      <List disablePadding>
        {states.prevData.map((d, i) => (
          <LocalMessageLocation id={`r[${i}]`} key={i}>
            <KeywordRuleLine className={classOf(d, i)} r_key={`r[${i}]`} prevData={d} disabled={disabled} actionButton={actionBtn(d, i)} />
          </LocalMessageLocation>
        ))}
      </List>
    </Box>
  )
}

const krSchema = Yup.object({
  regex: Yup.string().nullable().test('is-valid-regex', 'Context Pattern value is not a valid regular expression', (x:string) => {
    try {
      x && new RegExp(x)
      return true
    } catch {
      return false
    }
  }),
  words: Yup.array(Yup.string()).nullable(),
  scoreAdjustment: Yup.number().required('Context Score is required'),
})

const KeywordRuleLine : FC<{ 
  className?: string,
  r_key: string,
  prevData: KeywordRule, 
  actionButton: ReactElement|null,
  disabled?: boolean,
}> = ({ className, r_key, prevData, actionButton, disabled }) => {
  const [states, setStates] = useState({
    prevData,
    stage: {...prevData},
    errors: {} as any,
    touched: {} as any,
    isDirty: false,
  })
  useEffect(() => setStates(v => ({ prevData, stage: {...prevData}, errors: {} as any, touched: {} as any, isDirty: false, })), [prevData])
  const { validate, fieldProps } = usePlainStateFormUtils(states, setStates, krSchema)

  const {informDirty} = useLmStatesPropagate({
    key: r_key,
    asLowerStage: {
      collectStates: {
        handler() {
          return () => {
            const {promise, resolve, reject} = promiseWithResolvers()
            setStates(v => {
              const errors = validate(v.stage) ?? {}
              if (!errors['regex'] && !errors['words']) {
                const isUsingWords = v.stage.regex == null
                if (isUsingWords) {
                  if (!v.stage.words || v.stage.words.length === 0) {
                    errors['words'] = 'Context Keywords is required'
                  }
                } else {
                  if (!v.stage.regex) {
                    errors['regex'] = 'Context Pattern is required'
                  }
                  v.stage.words = null
                }
              }
              let ks = Object.keys(errors)
              if (ks.length > 0) {
                reject(errors[ks[0]])
              } else {
                resolve({
                  origin: v.prevData,
                  newValue: (!keywordRuleDirty(v)) ? v.prevData : v.stage 
                })
              }
              ks = Object.keys(v.stage)
              return {...v, errors, touched: ks.reduce((acc, k) => ({...acc, [k]:true}), {} as any) }
            })
            return promise
          }
        },
        deps: []
      }
    }
  })
  
  const checkDirty = (v:typeof states) => {
    const isDirty = keywordRuleDirty(v);
    informDirty(isDirty);
    v.isDirty = isDirty;
  }
  checkDirty(states)
  const isUsingWords = states.stage.regex == null
  const radioValue = isUsingWords ? 'words' : 'regex'
  const radioHandleChange = (e:any, value:string) => {
    if (disabled) { return }
    if (value === radioValue) { return }
    if (value === 'words') {
      setStates(v => {
        v.stage.regex = null
        v.stage.words = [...(v.prevData.words ??[])]
        delete v.errors['regex']
        delete v.touched['regex']
        checkDirty(v)
        return {...v}
      })
    } else {
      setStates(v => {
        v.stage.words = null
        v.stage.regex = v.prevData.regex ?? ''
        delete v.errors['words']
        delete v.touched['words']
        checkDirty(v)
        return {...v}
      })
    }
  }
  return (
    <ListItem className={className ?? (states.isDirty ? 'dirty' : undefined)}>
      <Stack direction="row" flexWrap="wrap" alignItems="center" width="100%" marginBottom={2}>
        <FormControl sx={{ mr: 'auto'}} size="small">
          <FormLabel>Type</FormLabel>
          <RadioGroup row value={radioValue} onChange={radioHandleChange}>
            <FormControlLabel value="words" label="Context Keywords" control={<Radio />} disabled={disabled}/>
            <FormControlLabel value="regex" label="Context Pattern" control={<Radio />} disabled={disabled}/>
          </RadioGroup>
        </FormControl>
        {actionButton}
        <Box width="100%" marginBottom={2}>
          {isUsingWords
            ? <StringArrayTextField list={states.stage.words ?? []} stackChips="row" disabled={disabled}   
                setList={(ls) => setStates(v => {
                  const words = [...new Set(ls)]
                  const v1 = ({...v, 
                    stage: {...v.stage, words},
                    touched: {...v.touched, words: true},
                    ...(words.length === 0 ? {errors: {...v.errors, words: 'Context Keywords is required'}} : (delete v.errors['words'], {errors: {...v.errors}}))
                  })
                  checkDirty(v1)
                  return v1
                })} 
                placeholder="Add a new value (case insensitive) and press the Enter key to confirm..."
                error={Boolean(states.touched['words'] && states.errors['words'])}
                helperText={states.touched['words'] && states.errors['words']}
              />
            : <TextField fullWidth multiline {...fieldProps('regex')} placeholder="type Regular Expression in here..." disabled={disabled}/>}
        </Box>
        <Box marginRight={8}>

        </Box>
        <TextField variant="standard" label="Context Score" sx={{ width: 140, alignSelf: 'end', '& p.MuiFormHelperText-root.Mui-error': { width: 200 } }} type="number" disabled={disabled}
          {...fieldProps('scoreAdjustment', (x: any) => {
            if (!x) { return undefined }
            let n = +x
            if (Number.isNaN(n)) { return 0 }
            n = parseFloat(n.toFixed(2))
            return (
                n < 0 ? 0 
              : n > 1 ? 1 
              : n)
          })} 
          inputProps={{ step: 0.01, max: 1, min: 0, }}
        />
      </Stack>
    </ListItem>
  )
}

const keywordRuleDirty = (x: {prevData: KeywordRule; stage:KeywordRule}) => {
  return (
    x.prevData.regex !== x.stage.regex ||
    x.prevData.scoreAdjustment !== x.stage.scoreAdjustment ||
    !!x.prevData.words !== !!x.stage.words ||
    (!!x.prevData.words && !(
      x.prevData.words.length === x.stage.words!.length && x.prevData.words.every(w => x.stage.words!.indexOf(w) > -1)))
    )
}


export const useOptionsData = () => {  
  const { data: classificationData } = useGetMainList<SecondaryClassificationResponseDto&RecPatch>('settings/governance/data-classification', pages(), sorts(), {})
  const { data: ruleMapData } = useGetOne<{map:Record<string, EncryptionRule[]>,id:any}>('file-encrypt-rule-map', DataKindCategory.Database)
  
  const {classifications, clssMap} = 
    useMemo(() => {
      const classifications: SimpleOption[] = Object.values<SecondaryClassificationResponseDto>(classificationData || {})
        .filter(c => c.primaryClassification !== PrimaryClassification.Public)
        .map(c => ({
          ...c, 
          key: c.secondaryClassificationId || -1, 
          text: `${c.primaryClassificationText} - ${c.name}` || ''
        })).sort((a, b) => 
                ((a.primaryClassification || 0) - (b.primaryClassification || 0)) 
              || ((a.secondaryClassificationId || 0) - (b.secondaryClassificationId || 0)))
      const clssMap = classifications.reduce((acc, c) => ({...acc, [c.key]: c }), {} as Record<string, SimpleOption>)
      return { classifications, clssMap }
    }, [classificationData])


  return { classifications, clssMap, ruleMap: ruleMapData?.map || {} }
}




